import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { notifications } from '@mantine/notifications';
import { Modal, Button, ControlledInput, ControlledFileInput } from '@shared/ui';
import { useUpdateHomeTile } from '../../hooks/useHomeTiles';
import { editHomeTileFormSchema } from '../../utils/homeTileValidation';
import type { HomeTile, UpdateHomeTileDto } from '../../types';
import './EditHomeTileModal.css';

interface EditHomeTileModalProps {
  isOpen: boolean;
  onClose: () => void;
  homeTile: HomeTile | null;
}

type FileInputValue = File | { id: number; url: string };

interface EditHomeTileFormData {
  name: string;
  image?: FileInputValue | null;
}

export default function EditHomeTileModal({ isOpen, onClose, homeTile }: EditHomeTileModalProps) {
  const resolver = yupResolver(editHomeTileFormSchema) as any;

  const { handleSubmit, formState: { errors }, reset, control } = useForm<EditHomeTileFormData>({
    resolver,
    defaultValues: {
      name: homeTile?.name || '',
      image: homeTile?.img ? { id: 0, url: homeTile.img } : null,
    },
  });
  const updateHomeTile = useUpdateHomeTile();

  useEffect(() => {
    if (homeTile) {
      reset({
        name: homeTile.name,
        image: homeTile.img ? { id: 0, url: homeTile.img } : null,
      });
    }
  }, [homeTile, reset]);

  const onSubmit = async (data: EditHomeTileFormData) => {
    if (!homeTile) return;

    try {
      const updateData: UpdateHomeTileDto = {
        name: data.name,
      };

      const imageFile = data.image instanceof File ? data.image : undefined;

      // Определяем, было ли изображение удалено
      // Если изначально было изображение, а сейчас null - значит удалено
      const hadImage = homeTile.img && homeTile.img.length > 0;
      const imageRemoved = hadImage && data.image === null;

      if (imageRemoved) {
        updateData.shouldDeleteImage = true;
      }

      await updateHomeTile.mutateAsync({
        id: homeTile.id,
        data: updateData,
        file: imageFile,
      });

      notifications.show({
        title: 'Успешно',
        message: 'Плитка успешно обновлена',
        color: 'green',
      });

      reset();
      onClose();
    } catch (error) {
      notifications.show({
        title: 'Ошибка',
        message: 'Ошибка при обновлении плитки',
        color: 'red',
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Редактировать плитку"
      size={400}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="edit-home-tile-form">
        <div className="form-group">
          <ControlledInput
            id="name"
            type="text"
            label="Название *"
            name="name"
            control={control}
            error={errors.name}
          />

        </div>

        <div className="form-group">
          <ControlledFileInput
            id="image"
            name="image"
            label="Изображение"
            accept="image/*"
            control={control}
            error={errors.image}
          />
        </div>

        <div className="edit-home-tile-form__actions">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            className="edit-home-tile-form__cancel-button"
          >
            Отмена
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={updateHomeTile.isPending}
            disabled={updateHomeTile.isPending}
            className="edit-home-tile-form__submit-button"
          >
            Сохранить
          </Button>
        </div>
      </form>
    </Modal>
  );
}

